/**
 * EditPaymentDateDialog (Iter 80)
 *
 * Primary-Admin-only narrow correction of `payment_date`. Cash / TDS /
 * mode / ref / TDS lifecycle are NEVER touched by this flow.
 *
 * Backend: PATCH /api/billing/payment/{id}/edit-date
 *   body: { new_payment_date, reason, note, bank_reference? }
 *
 * Filing gates:
 *   - parent invoice GST-Filed → blocks
 *   - TDS status Claimed / Adjusted → blocks
 *   - cross-FY edit → blocks (operator must void + re-record)
 */
import { useEffect, useState } from "react";
import { CalendarBlank, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const REASONS = [
    { value: "TypoCorrection",          label: "Typo correction — wrong date typed" },
    { value: "BankClearedDateMismatch", label: "Bank cleared on a different date" },
    { value: "Other",                   label: "Other (substantively explain in the note)" },
];

const NOTE_MIN = 5;

function isoToday(dateLike) {
    if (!dateLike) return "";
    const s = typeof dateLike === "string" ? dateLike : "";
    return s.slice(0, 10);
}

function indianFy(iso) {
    if (!iso || iso.length < 7) return "";
    const y = parseInt(iso.slice(0, 4), 10);
    const m = parseInt(iso.slice(5, 7), 10);
    if (Number.isNaN(y) || Number.isNaN(m)) return "";
    const start = m >= 4 ? y : y - 1;
    return `${start}-${String(start + 1).slice(-2)}`;
}

export default function EditPaymentDateDialog({ open, onOpenChange, payment, onDone }) {
    const [newDate, setNewDate] = useState("");
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [bankRef, setBankRef] = useState("");
    const [busy, setBusy] = useState(false);
    const [mmActive, setMmActive] = useState(false);

    useEffect(() => {
        if (!open) {
            setNewDate(""); setReason(""); setNote(""); setBankRef(""); setBusy(false);
            return;
        }
        setNewDate(isoToday(payment?.payment_date));
        setReason("");
        setNote("");
        setBankRef("");
        // Iter 80.3 — fetch tenant Migration Mode status so the cross-FY
        // banner switches between "blocked" (rose) and "permitted under
        // Migration Mode" (amber).
        api.get("/billing/migration-mode/status")
            .then((r) => setMmActive(r.data?.state === "active"))
            .catch(() => setMmActive(false));
    }, [open, payment?.id, payment?.payment_date]);

    const oldIso = isoToday(payment?.payment_date);
    const oldFy = indianFy(oldIso);
    const newFy = indianFy(newDate);
    const willCrossFy = oldFy && newFy && oldFy !== newFy;
    const isNoop = oldIso && newDate && oldIso === newDate;

    const valid = !!newDate && !!reason && note.trim().length >= NOTE_MIN;
    const isDirty = !!reason || note.length > 0 || newDate !== oldIso;

    const submit = async () => {
        if (!valid) return;
        setBusy(true);
        try {
            const body = {
                new_payment_date: newDate,
                reason, note: note.trim(),
                ...(bankRef.trim() ? { bank_reference: bankRef.trim() } : {}),
            };
            const { data } = await api.patch(
                `/billing/payment/${payment.id}/edit-date`, body,
            );
            if (data?.noop) {
                toast.success("No-op date edit recorded (audit-only — date unchanged)");
            } else {
                toast.success(`Payment date updated to ${formatDate(newDate)}`);
            }
            onDone?.();
            onOpenChange?.(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to update payment date"));
        } finally {
            setBusy(false);
        }
    };

    if (!payment) return null;

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-lg"
            testId="edit-payment-date-dialog"
            srTitle="Edit payment date"
            srDescription="Primary-Admin-only date-only correction of a settlement row. Cash and TDS are NOT touched. Full audit trail."
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <CalendarBlank size={18} weight="duotone" className="text-amber-600" />
                        Edit payment date
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200">
                            <ShieldCheck size={9} weight="fill" /> Primary Admin
                        </span>
                    </div>
                    <div className="text-xs text-slate-500">
                        Date-only correction — cash, TDS, mode, reference, TDS lifecycle stay intact.
                        Pushes the old date onto the row's date-history audit trail.
                    </div>
                </div>
            }
        >
            <div className="space-y-4 py-2">
                {/* Existing row context */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-700 space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="reedos-eyebrow text-[10px]">Current row</span>
                        <span className="text-[10px] text-slate-400 font-mono">{payment.id}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span>Date <b className="text-slate-900">{formatDate(payment.payment_date)}</b></span>
                        <span>·</span>
                        <span>FY <b className="text-slate-900">{oldFy || "—"}</b></span>
                    </div>
                </div>

                {/* New date input */}
                <div>
                    <Label className="text-xs font-semibold">New payment date *</Label>
                    <Input
                        type="date"
                        value={newDate}
                        onChange={(e) => setNewDate(e.target.value)}
                        className="mt-1"
                        data-testid="edit-payment-date-input"
                    />
                    {newDate && (
                        <div className="text-[11px] text-slate-600 mt-1.5 flex items-center gap-2">
                            <span>FY {newFy || "—"}</span>
                            <ArrowRight size={10} className="text-slate-400" />
                            {willCrossFy && !mmActive && (
                                <span className="text-rose-700 font-semibold" data-testid="edit-payment-date-cross-fy-warning">
                                    Cross-FY edit — blocked. Enable Migration Mode from Admin → Migration Mode to permit historical corrections.
                                </span>
                            )}
                            {willCrossFy && mmActive && (
                                <span className="text-amber-700 font-semibold" data-testid="edit-payment-date-cross-fy-mm-allowed">
                                    Cross-FY edit permitted under Migration Mode — this will be audit-tagged.
                                </span>
                            )}
                            {isNoop && (
                                <span className="text-slate-500" data-testid="edit-payment-date-noop-info">
                                    Same as current — submitting will record an audit-only no-op.
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Reason */}
                <div>
                    <Label className="text-xs font-semibold">Correction reason *</Label>
                    <select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                        data-testid="edit-payment-date-reason-select"
                    >
                        <option value="">— Pick a reason —</option>
                        {REASONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </select>
                </div>

                {/* Note */}
                <div>
                    <Label className="text-xs font-semibold">Operator note *</Label>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="What was wrongly entered, supporting bank-statement / Form 16A line…"
                        rows={3}
                        maxLength={1000}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                        data-testid="edit-payment-date-note-input"
                    />
                    <div className="text-[10px] text-slate-400 mt-1">
                        {note.length} / 1000 · min {NOTE_MIN} characters
                    </div>
                </div>

                {/* Bank reference (optional) */}
                <div>
                    <Label className="text-xs font-semibold">Bank statement / Form 16A reference <span className="text-slate-400">(optional)</span></Label>
                    <Input
                        type="text"
                        value={bankRef}
                        onChange={(e) => setBankRef(e.target.value)}
                        placeholder="e.g. HDFC stmt 2026-02-14 line 88"
                        className="mt-1"
                        maxLength={200}
                        data-testid="edit-payment-date-bank-ref-input"
                    />
                </div>
            </div>
            <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                <FormDialogCancel disabled={busy} testId="edit-payment-date-cancel-btn" />
                <Button
                    onClick={submit}
                    disabled={busy || !valid}
                    className="bg-amber-600 hover:bg-amber-500 text-white gap-1.5"
                    data-testid="edit-payment-date-confirm-btn"
                >
                    <CalendarBlank size={14} />
                    {busy ? "Saving…" : isNoop ? "Record no-op audit" : "Save corrected date"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
